import { useState } from 'react';
import useReveal from '../hooks/useReveal';

const calculatorOptions = [
  {
    id: 'lumpsum',
    title: 'Lumpsum',
    summary: 'See how a one-time investment can compound over time.',
  },
  {
    id: 'sip',
    title: 'SIP',
    summary: 'Estimate the future value of disciplined monthly or yearly investing.',
  },
  {
    id: 'goalLumpsum',
    title: 'Goal Planning Lumpsum',
    summary: 'Work backwards from a target corpus to a one-time investment required today.',
  },
  {
    id: 'goalSip',
    title: 'Goal Planning SIP',
    summary: 'Find the monthly or yearly contribution needed to reach a target amount.',
  },
  {
    id: 'timeOneTime',
    title: 'Time Duration One Time',
    summary: 'Find how long a single investment may take to reach your target wealth.',
  },
  {
    id: 'timeRegular',
    title: 'Time Duration Regular',
    summary: 'Estimate how many months or years your periodic investments need.',
  },
  {
    id: 'childEducation',
    title: 'Children Education Planner',
    summary: 'Project higher-education cost and the SIP required to fund it.',
  },
  {
    id: 'stepUp',
    title: 'Step Up Calculator',
    summary: 'Measure how annual SIP increases can lift your final corpus.',
  },
];

const defaultForms = {
  lumpsum: {
    investment: '100000',
    returnRate: '12',
    tenure: '10',
  },
  sip: {
    frequency: 'monthly',
    amount: '10000',
    returnRate: '12',
    tenure: '10',
  },
  goalLumpsum: {
    targetWealth: '5000000',
    returnRate: '12',
    tenure: '10',
    adjustInflation: true,
    inflationRate: '6',
  },
  goalSip: {
    frequency: 'monthly',
    targetWealth: '5000000',
    returnRate: '12',
    tenure: '10',
    adjustInflation: true,
    inflationRate: '6',
  },
  timeOneTime: {
    targetWealth: '1000000',
    investment: '250000',
    returnRate: '12',
  },
  timeRegular: {
    frequency: 'monthly',
    targetWealth: '1000000',
    amount: '10000',
    returnRate: '12',
  },
  childEducation: {
    currentAge: '5',
    higherEducationAge: '18',
    annualReturn: '12',
    presentCost: '1500000',
    educationInflation: '8',
  },
  stepUp: {
    monthlyInvestment: '5000',
    annualStepUp: '10',
    expectedReturn: '12',
    tenure: '15',
  },
};

const toNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

const decimalFormatter = new Intl.NumberFormat('en-IN', {
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => currencyFormatter.format(Number.isFinite(value) ? value : 0);

const formatNumber = (value) => decimalFormatter.format(Number.isFinite(value) ? value : 0);

const getPeriodicMeta = (frequency) => {
  if (frequency === 'yearly') {
    return {
      periodsPerYear: 1,
      periodLabel: 'year',
      contributionLabel: 'Yearly investment',
    };
  }

  return {
    periodsPerYear: 12,
    periodLabel: 'month',
    contributionLabel: 'Monthly investment',
  };
};

const futureValueOfLumpsum = (investment, annualRate, years) => {
  if (investment <= 0 || years <= 0) {
    return Math.max(investment, 0);
  }

  return investment * Math.pow(1 + annualRate / 100, years);
};

const futureValueOfRecurring = (payment, annualRate, years, frequency) => {
  const { periodsPerYear } = getPeriodicMeta(frequency);
  const periods = Math.max(0, Math.round(years * periodsPerYear));

  if (payment <= 0 || periods === 0) {
    return 0;
  }

  const periodicRate = annualRate / 100 / periodsPerYear;

  if (periodicRate === 0) {
    return payment * periods;
  }

  let futureValue = 0;
  for (let periodIndex = 0; periodIndex < periods; periodIndex += 1) {
    futureValue += payment * Math.pow(1 + periodicRate, periods - periodIndex);
  }

  return futureValue;
};

const requiredRecurringContribution = (targetValue, annualRate, years, frequency) => {
  const { periodsPerYear } = getPeriodicMeta(frequency);
  const periods = Math.max(0, Math.round(years * periodsPerYear));

  if (targetValue <= 0 || periods === 0) {
    return 0;
  }

  const periodicRate = annualRate / 100 / periodsPerYear;

  if (periodicRate === 0) {
    return targetValue / periods;
  }

  let low = 0;
  let high = targetValue;

  while (futureValueOfRecurring(high, annualRate, years, frequency) < targetValue) {
    high *= 2;
  }

  for (let index = 0; index < 80; index += 1) {
    const mid = (low + high) / 2;
    if (futureValueOfRecurring(mid, annualRate, years, frequency) >= targetValue) {
      high = mid;
    } else {
      low = mid;
    }
  }

  return high;
};

const requiredLumpsum = (targetValue, annualRate, years) => {
  if (targetValue <= 0) {
    return 0;
  }

  if (years <= 0) {
    return targetValue;
  }

  return targetValue / Math.pow(1 + annualRate / 100, years);
};

const periodsToReachRecurringTarget = (targetValue, payment, annualRate, frequency) => {
  const { periodsPerYear } = getPeriodicMeta(frequency);
  const periodicRate = annualRate / 100 / periodsPerYear;
  const maxPeriods = periodsPerYear * 100;

  if (targetValue <= 0) {
    return 0;
  }

  if (payment <= 0) {
    return null;
  }

  if (periodicRate <= 0) {
    return Math.ceil(targetValue / payment);
  }

  let futureValue = 0;
  for (let periods = 1; periods <= maxPeriods; periods += 1) {
    futureValue = futureValue * (1 + periodicRate) + payment * (1 + periodicRate);
    if (futureValue >= targetValue) {
      return periods;
    }
  }

  return null;
};

const durationFromYears = (years) => {
  if (!Number.isFinite(years) || years < 0) {
    return 'Not reachable with the current inputs';
  }

  const totalMonths = Math.round(years * 12);
  const fullYears = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  if (fullYears === 0) {
    return `${months} month${months === 1 ? '' : 's'}`;
  }

  if (months === 0) {
    return `${fullYears} year${fullYears === 1 ? '' : 's'}`;
  }

  return `${fullYears} year${fullYears === 1 ? '' : 's'} ${months} month${months === 1 ? '' : 's'}`;
};

const durationFromPeriods = (periods, frequency) => {
  if (periods === null) {
    return 'Not reachable within 100 years';
  }

  if (frequency === 'yearly') {
    return `${periods} year${periods === 1 ? '' : 's'}`;
  }

  return durationFromYears(periods / 12);
};

const stepUpProjection = (monthlyInvestment, annualStepUp, expectedReturn, years) => {
  const periods = Math.max(0, Math.round(years * 12));
  const monthlyRate = expectedReturn / 100 / 12;
  const stepFactor = 1 + annualStepUp / 100;
  let investedAmount = 0;
  let futureValue = 0;

  for (let month = 0; month < periods; month += 1) {
    const yearlyBand = Math.floor(month / 12);
    const contribution = monthlyInvestment * Math.pow(stepFactor, yearlyBand);
    investedAmount += contribution;

    if (monthlyRate === 0) {
      futureValue += contribution;
    } else {
      futureValue += contribution * Math.pow(1 + monthlyRate, periods - month);
    }
  }

  return {
    investedAmount,
    futureValue,
  };
};

const calculatorsById = Object.fromEntries(calculatorOptions.map((option) => [option.id, option]));

const ResultCard = ({ label, value, detail, accent = false }) => (
  <article className={`calculator-result-card${accent ? ' accent' : ''}`}>
    <span>{label}</span>
    <strong>{value}</strong>
    {detail ? <p>{detail}</p> : null}
  </article>
);

const Calculators = () => {
  useReveal('.fade-in');

  const [activeCalculator, setActiveCalculator] = useState('lumpsum');
  const [forms, setForms] = useState(defaultForms);

  const updateField = (calculatorId, key, value) => {
    setForms((current) => ({
      ...current,
      [calculatorId]: {
        ...current[calculatorId],
        [key]: value,
      },
    }));
  };

  const renderLumpsum = () => {
    const values = forms.lumpsum;
    const investment = toNumber(values.investment);
    const returnRate = toNumber(values.returnRate);
    const tenure = toNumber(values.tenure);
    const futureValue = futureValueOfLumpsum(investment, returnRate, tenure);
    const wealthGain = futureValue - investment;

    return {
      eyebrow: 'One-time investment calculator',
      heading: 'Project the future value of a lumpsum investment.',
      description:
        'This uses standard compound growth logic similar to the Finology lumpsum calculator: invest once today and let compounding do the rest.',
      form: (
        <div className="calculator-form-grid">
          <label className="calculator-field">
            <span>Investment Amount</span>
            <input type="number" min="0" value={values.investment} onChange={(event) => updateField('lumpsum', 'investment', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>Expected Return (P.A)</span>
            <input type="number" min="0" step="0.1" value={values.returnRate} onChange={(event) => updateField('lumpsum', 'returnRate', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>Tenure (Years)</span>
            <input type="number" min="0" step="0.5" value={values.tenure} onChange={(event) => updateField('lumpsum', 'tenure', event.target.value)} />
          </label>
        </div>
      ),
      results: (
        <>
          <ResultCard label="Projected Future Value" value={formatCurrency(futureValue)} detail={`At ${formatNumber(returnRate)}% for ${formatNumber(tenure)} years`} accent />
          <ResultCard label="Invested Amount" value={formatCurrency(investment)} />
          <ResultCard label="Estimated Wealth Gain" value={formatCurrency(wealthGain)} />
        </>
      ),
      footnote: 'Formula used: future value = investment × (1 + return)^tenure.',
    };
  };

  const renderSip = () => {
    const values = forms.sip;
    const amount = toNumber(values.amount);
    const returnRate = toNumber(values.returnRate);
    const tenure = toNumber(values.tenure);
    const futureValue = futureValueOfRecurring(amount, returnRate, tenure, values.frequency);
    const { periodsPerYear, contributionLabel } = getPeriodicMeta(values.frequency);
    const investedAmount = amount * Math.round(tenure * periodsPerYear);

    return {
      eyebrow: 'Regular investment calculator',
      heading: 'Estimate what a disciplined SIP can become.',
      description:
        'Choose monthly or yearly contributions and see the projected corpus, total invested amount, and gain from compounding.',
      form: (
        <div className="calculator-form-grid">
          <label className="calculator-field">
            <span>Frequency</span>
            <select value={values.frequency} onChange={(event) => updateField('sip', 'frequency', event.target.value)}>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </label>
          <label className="calculator-field">
            <span>{contributionLabel}</span>
            <input type="number" min="0" value={values.amount} onChange={(event) => updateField('sip', 'amount', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>Expected Return (P.A)</span>
            <input type="number" min="0" step="0.1" value={values.returnRate} onChange={(event) => updateField('sip', 'returnRate', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>Tenure (Years)</span>
            <input type="number" min="0" step="0.5" value={values.tenure} onChange={(event) => updateField('sip', 'tenure', event.target.value)} />
          </label>
        </div>
      ),
      results: (
        <>
          <ResultCard label="Projected Wealth" value={formatCurrency(futureValue)} detail={`${values.frequency === 'monthly' ? 'Monthly' : 'Yearly'} contribution plan`} accent />
          <ResultCard label="Total Invested" value={formatCurrency(investedAmount)} />
          <ResultCard label="Estimated Returns" value={formatCurrency(futureValue - investedAmount)} />
        </>
      ),
      footnote: 'SIP values are calculated as recurring contributions compounded every selected period.',
    };
  };

  const renderGoalLumpsum = () => {
    const values = forms.goalLumpsum;
    const targetWealth = toNumber(values.targetWealth);
    const returnRate = toNumber(values.returnRate);
    const tenure = toNumber(values.tenure);
    const inflationRate = toNumber(values.inflationRate);
    const inflationAdjustedTarget = values.adjustInflation
      ? targetWealth * Math.pow(1 + inflationRate / 100, tenure)
      : targetWealth;
    const requiredNow = requiredLumpsum(inflationAdjustedTarget, returnRate, tenure);

    return {
      eyebrow: 'Goal planning for one-time investing',
      heading: 'Find the lumpsum you need today for a future goal.',
      description:
        'Start with your target corpus, optionally inflate that goal, then discount it using your expected return to estimate the present investment required.',
      form: (
        <div className="calculator-form-grid">
          <label className="calculator-field">
            <span>Target Wealth</span>
            <input type="number" min="0" value={values.targetWealth} onChange={(event) => updateField('goalLumpsum', 'targetWealth', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>Expected Return (P.A)</span>
            <input type="number" min="0" step="0.1" value={values.returnRate} onChange={(event) => updateField('goalLumpsum', 'returnRate', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>Tenure (Years)</span>
            <input type="number" min="0" step="0.5" value={values.tenure} onChange={(event) => updateField('goalLumpsum', 'tenure', event.target.value)} />
          </label>
          <label className="calculator-switch">
            <input type="checkbox" checked={values.adjustInflation} onChange={(event) => updateField('goalLumpsum', 'adjustInflation', event.target.checked)} />
            <span>Adjust target for inflation</span>
          </label>
          <label className="calculator-field">
            <span>Inflation Rate (P.A)</span>
            <input type="number" min="0" step="0.1" value={values.inflationRate} onChange={(event) => updateField('goalLumpsum', 'inflationRate', event.target.value)} disabled={!values.adjustInflation} />
          </label>
        </div>
      ),
      results: (
        <>
          <ResultCard label="Required One-Time Investment" value={formatCurrency(requiredNow)} detail="Amount needed today to stay on target" accent />
          <ResultCard label="Target Corpus" value={formatCurrency(targetWealth)} />
          <ResultCard label="Inflation Adjusted Goal" value={formatCurrency(inflationAdjustedTarget)} />
        </>
      ),
      footnote: 'When inflation is enabled, the target corpus is first grown by inflation and then discounted using expected return.',
    };
  };

  const renderGoalSip = () => {
    const values = forms.goalSip;
    const targetWealth = toNumber(values.targetWealth);
    const returnRate = toNumber(values.returnRate);
    const tenure = toNumber(values.tenure);
    const inflationRate = toNumber(values.inflationRate);
    const inflationAdjustedTarget = values.adjustInflation
      ? targetWealth * Math.pow(1 + inflationRate / 100, tenure)
      : targetWealth;
    const requiredContribution = requiredRecurringContribution(
      inflationAdjustedTarget,
      returnRate,
      tenure,
      values.frequency,
    );
    const futureValue = futureValueOfRecurring(requiredContribution, returnRate, tenure, values.frequency);

    return {
      eyebrow: 'Goal planning for SIPs',
      heading: 'Compute the regular contribution needed to hit a future corpus.',
      description:
        'This is the reverse of a SIP calculator: choose a target amount and tenure, and the page solves for the monthly or yearly contribution required.',
      form: (
        <div className="calculator-form-grid">
          <label className="calculator-field">
            <span>Frequency</span>
            <select value={values.frequency} onChange={(event) => updateField('goalSip', 'frequency', event.target.value)}>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </label>
          <label className="calculator-field">
            <span>Target Wealth</span>
            <input type="number" min="0" value={values.targetWealth} onChange={(event) => updateField('goalSip', 'targetWealth', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>Expected Return (P.A)</span>
            <input type="number" min="0" step="0.1" value={values.returnRate} onChange={(event) => updateField('goalSip', 'returnRate', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>Tenure (Years)</span>
            <input type="number" min="0" step="0.5" value={values.tenure} onChange={(event) => updateField('goalSip', 'tenure', event.target.value)} />
          </label>
          <label className="calculator-switch">
            <input type="checkbox" checked={values.adjustInflation} onChange={(event) => updateField('goalSip', 'adjustInflation', event.target.checked)} />
            <span>Adjust target for inflation</span>
          </label>
          <label className="calculator-field">
            <span>Inflation Rate (P.A)</span>
            <input type="number" min="0" step="0.1" value={values.inflationRate} onChange={(event) => updateField('goalSip', 'inflationRate', event.target.value)} disabled={!values.adjustInflation} />
          </label>
        </div>
      ),
      results: (
        <>
          <ResultCard label={values.frequency === 'monthly' ? 'Required Monthly SIP' : 'Required Yearly SIP'} value={formatCurrency(requiredContribution)} detail="Estimated using recurring contribution compounding" accent />
          <ResultCard label="Inflation Adjusted Goal" value={formatCurrency(inflationAdjustedTarget)} />
          <ResultCard label="Projected Corpus At Plan Completion" value={formatCurrency(futureValue)} />
        </>
      ),
      footnote: 'The required contribution is solved numerically so it stays accurate for both monthly and yearly plans.',
    };
  };

  const renderTimeOneTime = () => {
    const values = forms.timeOneTime;
    const targetWealth = toNumber(values.targetWealth);
    const investment = toNumber(values.investment);
    const returnRate = toNumber(values.returnRate);
    let yearsToGoal = null;

    if (targetWealth <= investment && investment > 0) {
      yearsToGoal = 0;
    } else if (investment > 0 && returnRate > 0 && targetWealth > 0) {
      yearsToGoal = Math.log(targetWealth / investment) / Math.log(1 + returnRate / 100);
    }

    const projectedAfterTenYears = futureValueOfLumpsum(investment, returnRate, 10);

    return {
      eyebrow: 'Time duration for a one-time investment',
      heading: 'Measure how long a lumpsum needs to reach your target.',
      description:
        'Enter the target corpus, your current one-time investment, and the expected annual return to estimate the investment horizon.',
      form: (
        <div className="calculator-form-grid">
          <label className="calculator-field">
            <span>Target Wealth</span>
            <input type="number" min="0" value={values.targetWealth} onChange={(event) => updateField('timeOneTime', 'targetWealth', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>Single Investment Amount</span>
            <input type="number" min="0" value={values.investment} onChange={(event) => updateField('timeOneTime', 'investment', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>Expected Return (P.A)</span>
            <input type="number" min="0" step="0.1" value={values.returnRate} onChange={(event) => updateField('timeOneTime', 'returnRate', event.target.value)} />
          </label>
        </div>
      ),
      results: (
        <>
          <ResultCard label="Estimated Time Duration" value={durationFromYears(yearsToGoal)} detail={yearsToGoal === null ? 'Increase investment or return assumptions to reach the goal.' : `Approximately ${formatNumber(yearsToGoal)} years`} accent />
          <ResultCard label="Current Investment" value={formatCurrency(investment)} />
          <ResultCard label="10-Year Projection" value={formatCurrency(projectedAfterTenYears)} />
        </>
      ),
      footnote: 'The time horizon is derived from the compound growth equation for a one-time investment.',
    };
  };

  const renderTimeRegular = () => {
    const values = forms.timeRegular;
    const targetWealth = toNumber(values.targetWealth);
    const amount = toNumber(values.amount);
    const returnRate = toNumber(values.returnRate);
    const periods = periodsToReachRecurringTarget(targetWealth, amount, returnRate, values.frequency);
    const duration = durationFromPeriods(periods, values.frequency);
    const projectionInTenYears = futureValueOfRecurring(amount, returnRate, 10, values.frequency);
    const { contributionLabel } = getPeriodicMeta(values.frequency);

    return {
      eyebrow: 'Time duration for recurring investing',
      heading: 'Estimate when your regular investments can reach the goal.',
      description:
        'Switch between monthly and yearly contributions and the calculator will estimate the number of periods needed to hit the target corpus.',
      form: (
        <div className="calculator-form-grid">
          <label className="calculator-field">
            <span>Frequency</span>
            <select value={values.frequency} onChange={(event) => updateField('timeRegular', 'frequency', event.target.value)}>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </label>
          <label className="calculator-field">
            <span>Target Wealth</span>
            <input type="number" min="0" value={values.targetWealth} onChange={(event) => updateField('timeRegular', 'targetWealth', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>{contributionLabel}</span>
            <input type="number" min="0" value={values.amount} onChange={(event) => updateField('timeRegular', 'amount', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>Expected Return (P.A)</span>
            <input type="number" min="0" step="0.1" value={values.returnRate} onChange={(event) => updateField('timeRegular', 'returnRate', event.target.value)} />
          </label>
        </div>
      ),
      results: (
        <>
          <ResultCard label="Estimated Time Duration" value={duration} detail={periods === null ? 'Target is outside the 100-year calculation window.' : `${periods} contribution periods`} accent />
          <ResultCard label="Target Wealth" value={formatCurrency(targetWealth)} />
          <ResultCard label="10-Year Projection" value={formatCurrency(projectionInTenYears)} />
        </>
      ),
      footnote: 'The horizon is solved by simulating recurring contributions until the target corpus is reached.',
    };
  };

  const renderChildEducation = () => {
    const values = forms.childEducation;
    const currentAge = toNumber(values.currentAge);
    const higherEducationAge = toNumber(values.higherEducationAge);
    const annualReturn = toNumber(values.annualReturn);
    const presentCost = toNumber(values.presentCost);
    const educationInflation = toNumber(values.educationInflation);
    const yearsToGoal = Math.max(0, higherEducationAge - currentAge);
    const futureCost = presentCost * Math.pow(1 + educationInflation / 100, yearsToGoal);
    const monthlySaving = requiredRecurringContribution(futureCost, annualReturn, yearsToGoal, 'monthly');

    return {
      eyebrow: 'Children education planner',
      heading: 'Estimate the SIP required for a child’s higher education.',
      description:
        'This planner first inflates today’s education cost into the future and then calculates the monthly SIP needed to accumulate that corpus in time.',
      form: (
        <div className="calculator-form-grid">
          <label className="calculator-field">
            <span>Current Age of Child</span>
            <input type="number" min="0" value={values.currentAge} onChange={(event) => updateField('childEducation', 'currentAge', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>Age for Higher Education</span>
            <input type="number" min="0" value={values.higherEducationAge} onChange={(event) => updateField('childEducation', 'higherEducationAge', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>Expected Return (P.A)</span>
            <input type="number" min="0" step="0.1" value={values.annualReturn} onChange={(event) => updateField('childEducation', 'annualReturn', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>Present Cost of Higher Education</span>
            <input type="number" min="0" value={values.presentCost} onChange={(event) => updateField('childEducation', 'presentCost', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>Education Inflation (P.A)</span>
            <input type="number" min="0" step="0.1" value={values.educationInflation} onChange={(event) => updateField('childEducation', 'educationInflation', event.target.value)} />
          </label>
        </div>
      ),
      results: (
        <>
          <ResultCard label="Required Monthly SIP" value={formatCurrency(monthlySaving)} detail={`Saving window: ${yearsToGoal} years`} accent />
          <ResultCard label="Future Cost of Education" value={formatCurrency(futureCost)} />
          <ResultCard label="Present Cost" value={formatCurrency(presentCost)} />
        </>
      ),
      footnote: 'The planner assumes education cost rises annually by the selected inflation rate before calculating the SIP required.',
    };
  };

  const renderStepUp = () => {
    const values = forms.stepUp;
    const monthlyInvestment = toNumber(values.monthlyInvestment);
    const annualStepUp = toNumber(values.annualStepUp);
    const expectedReturn = toNumber(values.expectedReturn);
    const tenure = toNumber(values.tenure);
    const { investedAmount, futureValue } = stepUpProjection(monthlyInvestment, annualStepUp, expectedReturn, tenure);

    return {
      eyebrow: 'Step-up SIP calculator',
      heading: 'See how annual SIP increases can accelerate wealth creation.',
      description:
        'This follows the standard step-up SIP idea: increase the monthly amount every year and let each contribution compound until the end of the chosen tenure.',
      form: (
        <div className="calculator-form-grid">
          <label className="calculator-field">
            <span>Monthly Investment Amount</span>
            <input type="number" min="0" value={values.monthlyInvestment} onChange={(event) => updateField('stepUp', 'monthlyInvestment', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>Growth in Investment Amount (P.A)</span>
            <input type="number" min="0" step="0.1" value={values.annualStepUp} onChange={(event) => updateField('stepUp', 'annualStepUp', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>Expected Return (P.A)</span>
            <input type="number" min="0" step="0.1" value={values.expectedReturn} onChange={(event) => updateField('stepUp', 'expectedReturn', event.target.value)} />
          </label>
          <label className="calculator-field">
            <span>Tenure (Years)</span>
            <input type="number" min="0" step="0.5" value={values.tenure} onChange={(event) => updateField('stepUp', 'tenure', event.target.value)} />
          </label>
        </div>
      ),
      results: (
        <>
          <ResultCard label="Projected Wealth" value={formatCurrency(futureValue)} detail={`Assuming a ${formatNumber(annualStepUp)}% annual SIP step-up`} accent />
          <ResultCard label="Total Invested" value={formatCurrency(investedAmount)} />
          <ResultCard label="Estimated Returns" value={formatCurrency(futureValue - investedAmount)} />
        </>
      ),
      footnote: 'Monthly investments are stepped up once every 12 months and each monthly contribution compounds separately.',
    };
  };

  const renderers = {
    lumpsum: renderLumpsum,
    sip: renderSip,
    goalLumpsum: renderGoalLumpsum,
    goalSip: renderGoalSip,
    timeOneTime: renderTimeOneTime,
    timeRegular: renderTimeRegular,
    childEducation: renderChildEducation,
    stepUp: renderStepUp,
  };

  const activeContent = renderers[activeCalculator]();
  const activeMeta = calculatorsById[activeCalculator];

  return (
    <div className="calculators-page">
      <section className="calculators-hero">
        <div className="calculators-hero-copy fade-in">
          <span className="section-label">Financial Calculators</span>
          <h1>Smart planning tools for wealth, goals, and timelines.</h1>
          {/* <p>
            This page brings together the calculator set you requested: lumpsum, SIP,
            goal planning, time duration, child education planning, and step-up SIP.
            The inputs follow the same broad logic used in Finology&apos;s investment calculators.
          </p> */}
        </div>
      </section>

      <section className="calculators-layout">
        <aside className="calculators-sidebar fade-in">
          <h2>Choose a calculator</h2>
          <div className="calculators-tab-list">
            {calculatorOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`calculator-tab${activeCalculator === option.id ? ' active' : ''}`}
                onClick={() => setActiveCalculator(option.id)}
              >
                <strong>{option.title}</strong>
                <span>{option.summary}</span>
              </button>
            ))}
          </div>
        </aside>

        <section className="calculator-panel fade-in" aria-labelledby="calculator-heading">
          <span className="calculator-eyebrow">{activeContent.eyebrow}</span>
          <h2 id="calculator-heading">{activeMeta.title}</h2>
          <p className="calculator-description">{activeContent.description}</p>

          <div className="calculator-workspace">
            <div className="calculator-inputs">
              <h3>{activeContent.heading}</h3>
              {activeContent.form}
            </div>

            <div className="calculator-results">
              <h3>Results</h3>
              <div className="calculator-results-grid">{activeContent.results}</div>
            </div>
          </div>

          <p className="calculator-footnote">{activeContent.footnote}</p>
        </section>
      </section>
    </div>
  );
};

export default Calculators;